(function () {
  'use strict';

  /**
   * Nav hover panel — lightweight controller.
   * CSS handles visual reveal on :hover / :focus-within for .main-nav__child.
   * JS adds:
   *   - Pointer intent delay so the panel doesn't flash on quick mouse traversal
   *   - Esc to close the currently hovered/focused panel
   *   - data-nav-source attribute on clicks for analytics
   */

  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 220;
  const ROOT_SELECTOR = '.store-header--redesign .main-nav > li';
  const mediaQuery = window.matchMedia('(min-width: 1200px)');

  function addHoverIntent(li) {
    let openTimer = null;
    let closeTimer = null;
    const details = li.querySelector('details');

    function open() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      if (openTimer) return;
      openTimer = setTimeout(function () {
        li.setAttribute('data-hover-open', 'true');
        if (details && !details.open) details.open = true;
        openTimer = null;
      }, OPEN_DELAY);
    }
    function close() {
      if (openTimer) { clearTimeout(openTimer); openTimer = null; }
      if (closeTimer) return;
      closeTimer = setTimeout(function () {
        li.removeAttribute('data-hover-open');
        if (details && details.open) details.open = false;
        closeTimer = null;
      }, CLOSE_DELAY);
    }

    li.addEventListener('pointerenter', function () {
      if (mediaQuery.matches) open();
    });
    li.addEventListener('pointerleave', function () {
      if (mediaQuery.matches) close();
    });
  }

  function handleEscape(e) {
    if (e.key !== 'Escape') return;

    // Close any open desktop hover panel first
    const open = document.querySelector('.store-header--redesign .main-nav details[open]');
    if (open && mediaQuery.matches) {
      open.open = false;
      const summary = open.querySelector('summary a, summary');
      if (summary) summary.focus();
      return;
    }

    // Below desktop, Escape closes the mobile drawer if it's open
    if (!mediaQuery.matches) {
      const drawer = document.querySelector('.store-header--redesign .main-menu__disclosure[open]');
      if (drawer) {
        drawer.open = false;
        const toggle = drawer.querySelector('.main-menu__toggle');
        if (toggle) toggle.focus();
      }
    }
  }

  function tagAnalytics() {
    document.querySelectorAll('.store-header--redesign .main-nav__child a').forEach(function (a) {
      if (!a.hasAttribute('data-nav-source')) {
        a.setAttribute('data-nav-source', 'hover-panel');
      }
    });
    document.querySelectorAll('.store-header--redesign .main-nav > li > details > summary a, .store-header--redesign .main-nav > li > a').forEach(function (a) {
      if (!a.hasAttribute('data-nav-source')) {
        a.setAttribute('data-nav-source', 'top-level');
      }
    });
  }

  function bindCartPulse() {
    const bubble = document.querySelector('.store-header--redesign #cart-icon-bubble, .store-header--redesign .header__cart-count, .store-header--redesign .cart-count-bubble');
    if (!bubble) return;

    let lastCount = readCount(bubble);
    const observer = new MutationObserver(function () {
      const current = readCount(bubble);
      if (current !== lastCount && current > lastCount) {
        bubble.classList.remove('cart-badge--pulse', 'cart-count-bubble--pulse');
        // force reflow to restart animation
        void bubble.offsetWidth;
        bubble.classList.add('cart-badge--pulse', 'cart-count-bubble--pulse');
        setTimeout(function () {
          bubble.classList.remove('cart-badge--pulse', 'cart-count-bubble--pulse');
        }, 700);
      }
      lastCount = current;
    });
    observer.observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  function readCount(bubble) {
    const txt = (bubble.textContent || '').trim().replace(/\D/g, '');
    return parseInt(txt, 10) || 0;
  }

  /**
   * Cart badge live-refresh.
   * Several cart-add code paths live in this theme (product-form.js, cart-drawer.js,
   * and custom handleCartAction in theme.liquid used by reels/spool-it snippets).
   * Intercepting fetch() for cart mutation URLs is the only place that covers all of them.
   * On any /cart/(add|change|update|clear).js call, we re-fetch /cart.js and rewrite the
   * bubble with a structure that matches both the inline header render and the section file.
   */
  async function refreshCartCount() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!res.ok) return;
      const cart = await res.json();
      const count = cart.item_count || 0;
      if (count === 0) {
        bubble.innerHTML = '';
        return;
      }
      const display = count < 10000 ? count : count;
      const plural = count !== 1 ? 's' : '';
      bubble.innerHTML =
        '<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">' +
          '<span aria-hidden="true" class="header__cart-count-number">' + display + '</span>' +
          '<span class="visually-hidden">' + count + ' item' + plural + ' in cart</span>' +
        '</div>';
    } catch (e) { /* silent */ }
  }

  function patchFetchForCart() {
    if (!window.fetch || window.__chaosCartFetchPatched) return;
    window.__chaosCartFetchPatched = true;
    const orig = window.fetch;
    window.fetch = function (input, init) {
      const promise = orig.apply(this, arguments);
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        promise.then(function () { setTimeout(refreshCartCount, 60); }).catch(function () {});
      }
      return promise;
    };
  }

  /* ---------- Mobile drawer (saragosa-pattern, white surface) ---------- */
  const DRAWER_ID = 'nav-mobile-drawer';
  const DRAWER_OPEN_ATTR = 'data-open';
  const DRAWER_FOCUSABLES = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');
  let drawerLastFocused = null;

  function getDrawer() { return document.getElementById(DRAWER_ID); }
  function getDrawerBackdrop() { return document.querySelector('.nav-mobile-drawer-backdrop'); }

  function openDrawer() {
    const drawer = getDrawer();
    const backdrop = getDrawerBackdrop();
    if (!drawer) return;
    drawerLastFocused = document.activeElement;
    drawer.setAttribute(DRAWER_OPEN_ATTR, 'true');
    drawer.removeAttribute('aria-hidden');
    drawer.removeAttribute('inert');
    if (backdrop) {
      backdrop.setAttribute(DRAWER_OPEN_ATTR, 'true');
      backdrop.removeAttribute('hidden');
    }
    document.documentElement.classList.add('nav-mobile-drawer-open');
    // Sync aria-expanded on the trigger
    const trigger = document.querySelector('[data-mobile-drawer-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    // Focus the close button (not the first focusable, which would be the
    // logo — focusing the logo paints a visible focus ring around it on
    // every drawer open, which reads as visual noise. Close button is the
    // most-likely-next action anyway).
    const closeBtn = drawer.querySelector('[data-mobile-drawer-close]');
    if (closeBtn) setTimeout(() => closeBtn.focus(), 40);
  }

  function closeDrawer() {
    const drawer = getDrawer();
    const backdrop = getDrawerBackdrop();
    if (!drawer) return;
    drawer.removeAttribute(DRAWER_OPEN_ATTR);
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    if (backdrop) {
      backdrop.removeAttribute(DRAWER_OPEN_ATTR);
      backdrop.setAttribute('hidden', '');
    }
    document.documentElement.classList.remove('nav-mobile-drawer-open');
    const trigger = document.querySelector('[data-mobile-drawer-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (drawerLastFocused && typeof drawerLastFocused.focus === 'function') {
      drawerLastFocused.focus();
    }
  }

  function isDrawerOpen() {
    const drawer = getDrawer();
    return drawer && drawer.getAttribute(DRAWER_OPEN_ATTR) === 'true';
  }

  function trapDrawerTab(e) {
    if (e.key !== 'Tab' || !isDrawerOpen()) return;
    const drawer = getDrawer();
    const focusables = Array.from(drawer.querySelectorAll(DRAWER_FOCUSABLES))
      .filter(el => el.offsetParent !== null || el === document.activeElement);
    if (focusables.length === 0) { e.preventDefault(); return; }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !drawer.contains(active)) { e.preventDefault(); last.focus(); }
    } else {
      if (active === last) { e.preventDefault(); first.focus(); }
    }
  }

  function handleDrawerKey(e) {
    if (!isDrawerOpen()) return;
    if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
    if (e.key === 'Tab') trapDrawerTab(e);
  }

  /**
   * Portal the drawer + its backdrop to <body> at init so they live at
   * document-root level. Prevents stacking-context leakage from the
   * surrounding section group (header-group's wrapper, sticky elements,
   * announcement bar) — the drawer always wins z-index unconditionally.
   */
  function portalDrawerToBody() {
    const drawer = getDrawer();
    const backdrop = getDrawerBackdrop();
    if (drawer && drawer.parentNode !== document.body) {
      document.body.appendChild(drawer);
    }
    if (backdrop && backdrop.parentNode !== document.body) {
      document.body.appendChild(backdrop);
    }
  }

  function bindDrawerTriggers() {
    portalDrawerToBody();
    document.addEventListener('click', function (e) {
      const opener = e.target.closest('[data-mobile-drawer-trigger]');
      if (opener) { e.preventDefault(); openDrawer(); return; }
      const closer = e.target.closest('[data-mobile-drawer-close]');
      if (closer) { e.preventDefault(); closeDrawer(); }
    });
    document.addEventListener('keydown', handleDrawerKey);
    // bfcache safety — reset state on page restore
    window.addEventListener('pageshow', function (e) {
      if (e.persisted && isDrawerOpen()) closeDrawer();
    });
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(addHoverIntent);
    document.addEventListener('keydown', handleEscape);
    tagAnalytics();
    bindCartPulse();
    patchFetchForCart();
    bindDrawerTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
